/**
 * Guocali wish list (Horizon 19-09-26).
 * Config: <script id="GuocaliWishlistConfig"> in snippets/wishlist.liquid.
 *
 * - Hearts on every <product-card data-product-id> (collections, search, recommendations).
 * - "Add to wish list" button under the buy buttons on product pages.
 * - Heart link + count next to the cart icon in the header.
 * - Renders the list on /pages/wishlist ([data-gwl-page]).
 * - Guests: localStorage. Logged in: also saved to the customer metafield custom.wishlist
 *   via the signed sync endpoint (Google Apps Script web app; form-encoded beacon).
 */
(() => {
  if (window.GuocaliWishlist) return;

  const configEl = document.getElementById('GuocaliWishlistConfig');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch (error) {
    return;
  }

  if (config.enabled === false) return;
  const CARD_POSITION = config.cardPosition || 'top-right';

  const STORE_KEY = 'guocali_wishlist';
  const OWNER_KEY = 'guocali_wishlist_owner';
  const CHANGED_KEY = 'guocali_wishlist_changed';
  // The account copy is saved through the sync endpoint a moment after a change. For this long after
  // a change on this device, trust the local copy over the (possibly not yet updated) account.
  const LOCAL_TRUST_MS = 60 * 1000;
  const S = config.strings || {};

  const HEART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.6-9.2C.9 8 2.6 4.5 6.1 4.1c2.1-.2 3.8.9 4.9 2.5 1.1-1.6 2.8-2.7 4.9-2.5 3.5.4 5.2 3.9 3.7 7.2-2.1 4.6-9.6 9.2-9.6 9.2z" stroke-linejoin="round"/></svg>';

  /* ---------------- storage ---------------- */

  const readLocal = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((i) => i && i.id) : [];
    } catch (error) {
      return [];
    }
  };

  const writeLocal = (list) => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage blocked */
    }
  };

  const getOwner = () => {
    try {
      return localStorage.getItem(OWNER_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const setOwner = (value) => {
    try {
      if (value) localStorage.setItem(OWNER_KEY, value);
      else localStorage.removeItem(OWNER_KEY);
    } catch (error) {
      /* storage blocked */
    }
  };

  const norm = (item) => ({ id: String(item.id), handle: item.handle || '' });

  const unique = (list) => {
    const seen = new Set();
    return list.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  let items = [];
  const customerId = config.customerId ? String(config.customerId) : '';

  const init = () => {
    const local = readLocal().map(norm);
    const saved = (config.saved || []).map(norm);

    if (customerId) {
      if (getOwner() === customerId) {
        // Already synced on this device: the account is the source of truth,
        // unless this device changed the list in the last minute (save still in flight).
        let changedAt = 0;
        try {
          changedAt = Number(localStorage.getItem(CHANGED_KEY) || 0);
        } catch (error) {
          changedAt = 0;
        }
        if (Date.now() - changedAt < LOCAL_TRUST_MS) {
          items = local;
          scheduleSync();
        } else {
          items = saved;
        }
      } else {
        // First visit logged in on this device: merge the guest list into the account.
        items = unique([...saved, ...local]);
        setOwner(customerId);
        if (items.length !== saved.length) scheduleSync();
      }
    } else {
      if (getOwner()) {
        // Logged out after using an account on this device: start a fresh guest list.
        setOwner('');
        items = [];
      } else {
        items = local;
      }
    }
    writeLocal(items);
  };

  /* Sends the signed update to the sync endpoint (Google Apps Script web app).
   * sendBeacon first: it is a simple CORS request, survives page navigation and is not
   * affected by the 302 redirect Apps Script answers with. fetch() is the fallback. */
  const sendSigned = (url, payload) => {
    if (window.GuocaliWishlistDebug) console.log('[wishlist] sync ->', url, payload);
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch (error) {
      /* fall through to fetch */
    }
    fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload,
    }).catch(() => {});
  };
  window.GuocaliSendSigned = sendSigned;

  let syncTimer = 0;
  const scheduleSync = () => {
    if (!customerId || !config.endpoint || !config.sig) return;
    clearTimeout(syncTimer);
    syncTimer = window.setTimeout(sync, 700);
  };

  const sync = () => {
    const body = new URLSearchParams();
    body.set('customer_id', customerId);
    body.set('sig', config.sig);
    body.set('products_json', JSON.stringify(items.slice(0, 120).map((i) => `gid://shopify/Product/${i.id}`)));

    sendSigned(config.endpoint, body.toString());
  };

  const has = (id) => items.some((i) => i.id === String(id));

  const toggle = (id, handle) => {
    id = String(id);
    let added;
    if (has(id)) {
      items = items.filter((i) => i.id !== id);
      added = false;
    } else {
      items = [{ id, handle: handle || '' }, ...items];
      added = true;
    }
    writeLocal(items);
    try {
      localStorage.setItem(CHANGED_KEY, String(Date.now()));
    } catch (error) {
      /* storage blocked */
    }
    scheduleSync();
    refresh();
    toast(added ? S.toastAdded : S.toastRemoved, added);
    return added;
  };

  /* ---------------- UI helpers ---------------- */

  const handleFromUrl = (url) => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const makeHeart = (id, handle, extraClass) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `gwl-heart ${extraClass || ''}`.trim();
    button.dataset.gwlId = String(id);
    if (handle) button.dataset.gwlHandle = handle;
    button.innerHTML = HEART;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggle(button.dataset.gwlId, button.dataset.gwlHandle);
      button.classList.remove('gwl-heart--pop');
      void button.offsetWidth;
      button.classList.add('gwl-heart--pop');
    });
    return button;
  };

  const syncHeart = (button) => {
    const on = has(button.dataset.gwlId);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.setAttribute('aria-label', on ? S.added : S.add);
    button.title = on ? S.added : S.add;
  };

  /* ---------------- injection ---------------- */

  const injectCards = () => {
    if (CARD_POSITION === 'hidden') return;
    document.querySelectorAll('product-card[data-product-id]:not([data-gwl-ready])').forEach((card) => {
      const id = card.getAttribute('data-product-id');
      if (!id) return;
      const link = card.querySelector('a[href*="/products/"]');
      const handle = handleFromUrl(link && link.getAttribute('href'));
      card.setAttribute('data-gwl-ready', '');
      card.appendChild(makeHeart(id, handle, `gwl-card-heart gwl-card-heart--${CARD_POSITION}`));
    });
  };

  const injectProductButtons = () => {
    if (config.showProductButton === false) return;
    document
      .querySelectorAll('.buy-buttons-block product-form-component[data-product-id]:not([data-gwl-ready])')
      .forEach((form) => {
        const id = form.getAttribute('data-product-id');
        if (!id) return;
        form.setAttribute('data-gwl-ready', '');
        const handle = handleFromUrl(form.getAttribute('data-product-url'));

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gwl-product-btn';
        button.dataset.gwlId = String(id);
        button.dataset.gwlHandle = handle;
        button.innerHTML = `${HEART}<span data-gwl-label></span>`;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          toggle(button.dataset.gwlId, button.dataset.gwlHandle);
        });
        form.insertAdjacentElement('afterend', button);
      });
  };

  const injectHeader = () => {
    if (config.showHeader === false) return;
    document.querySelectorAll('cart-icon.header-actions__cart-icon').forEach((cartIcon) => {
      const parent = cartIcon.parentElement;
      if (!parent || parent.querySelector('.gwl-header-link')) return;
      const link = document.createElement('a');
      link.className = 'gwl-header-link header-actions__action';
      link.href = config.pageUrl;
      link.setAttribute('aria-label', S.title);
      link.title = S.title;
      link.innerHTML = `${HEART}<span class="gwl-header-count" hidden><span data-gwl-count></span></span>`;
      parent.insertBefore(link, cartIcon);
    });
  };

  const refresh = () => {
    document.querySelectorAll('.gwl-heart[data-gwl-id]').forEach(syncHeart);

    document.querySelectorAll('.gwl-product-btn').forEach((button) => {
      const on = has(button.dataset.gwlId);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = button.querySelector('[data-gwl-label]');
      if (label) label.textContent = on ? S.added : S.add;
    });

    document.querySelectorAll('.gwl-header-link').forEach((link) => {
      const count = link.querySelector('.gwl-header-count');
      const text = link.querySelector('[data-gwl-count]');
      link.classList.toggle('gwl-header-link--active', items.length > 0);
      if (count && text) {
        count.hidden = items.length === 0;
        text.textContent = String(items.length);
      }
    });
  };

  /* ---------------- toast ---------------- */

  let toastEl;
  let toastTimer = 0;
  const toast = (message, withLink) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'gwl-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    const text = document.createElement('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (withLink && !document.querySelector('[data-gwl-page]')) {
      const link = document.createElement('a');
      link.href = config.pageUrl;
      link.textContent = S.view;
      toastEl.appendChild(link);
    }
    toastEl.classList.add('gwl-toast--show');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toastEl.classList.remove('gwl-toast--show'), 2600);
  };

  /* ---------------- wish list page ---------------- */

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(config.locale || undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency || 'EUR',
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  };

  const productCache = new Map();
  const fetchProduct = (item) => {
    if (!item.handle) return Promise.resolve(null);
    if (productCache.has(item.handle)) return productCache.get(item.handle);
    const request = fetch(`${config.root}products/${encodeURIComponent(item.handle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productCache.set(item.handle, request);
    return request;
  };

  const imageUrl = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const renderPage = async () => {
    const page = document.querySelector('[data-gwl-page]');
    if (!page) return;

    const listEl = page.querySelector('[data-gwl-list]');
    const noteEl = page.querySelector('[data-gwl-note]');
    if (!listEl) return;

    if (noteEl) {
      if (!customerId && items.length) {
        noteEl.hidden = false;
        noteEl.innerHTML = '';
        noteEl.append(document.createTextNode(`${S.guestNote} `));
        const login = document.createElement('a');
        login.href = config.loginUrl;
        login.textContent = S.login;
        noteEl.appendChild(login);
      } else {
        noteEl.hidden = true;
      }
    }

    if (!items.length) {
      listEl.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'gwl-empty';
      const p = document.createElement('p');
      p.textContent = S.empty;
      const a = document.createElement('a');
      a.className = 'button';
      a.href = config.shopUrl;
      a.textContent = S.shop;
      empty.append(p, a);
      listEl.appendChild(empty);
      return;
    }

    const products = await Promise.all(items.map(fetchProduct));
    const grid = document.createElement('div');
    grid.className = 'gwl-grid';

    items.forEach((item, index) => {
      const product = products[index];
      if (!product) return;

      const card = document.createElement('div');
      card.className = 'gwl-item';

      const media = document.createElement('a');
      media.className = 'gwl-item__media';
      media.href = product.url;
      const image = product.featured_image || (product.images && product.images[0]);
      if (image) {
        const img = document.createElement('img');
        img.src = imageUrl(image, 600);
        img.alt = product.title;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const title = document.createElement('h3');
      title.className = 'gwl-item__title';
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.appendChild(titleLink);

      const price = document.createElement('div');
      price.className = 'gwl-item__price';
      price.textContent = formatMoney(product.price);
      if (product.compare_at_price && product.compare_at_price > product.price) {
        const was = document.createElement('s');
        was.textContent = formatMoney(product.compare_at_price);
        price.appendChild(was);
      }

      card.append(media, title, price);

      if (!product.available) {
        const sold = document.createElement('div');
        sold.className = 'gwl-item__soldout';
        sold.textContent = S.soldOut;
        card.appendChild(sold);
      }

      const heart = makeHeart(item.id, item.handle, 'gwl-card-heart');
      heart.addEventListener('click', () => window.setTimeout(renderPage, 150));
      card.appendChild(heart);
      syncHeart(heart);

      grid.appendChild(card);
    });

    listEl.innerHTML = '';
    listEl.appendChild(grid);
  };

  /* ---------------- boot ---------------- */

  const scan = () => {
    injectCards();
    injectProductButtons();
    injectHeader();
    refresh();
  };

  init();

  const start = () => {
    scan();
    renderPage();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan();
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.GuocaliWishlist = { has, toggle, items: () => items.slice() };
})();
